import {
  Alert,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useState } from "react";
import dayjs from "dayjs";
import { z } from "zod";
import { useTenantData } from "contexts/tenant";
import { useTenantTranslation } from "hooks/formatters";
import { ParameterSchema } from "./parameterSchema";
import { MonthlyTargetsPlot } from "./MonthlyTargetsPlot";

import { AutoRecipeMonthlyValueMode } from "store/api/generatedApi";
import {
  latestMonthlyChange,
  monthlyParameterValue,
} from "./monthlyParameterValues";
import { useOlderRows } from "./useOlderRows";

const intervalsSchema = z.array(
  z.object({ start: z.string(), end: z.string() })
);

export const TimeIntervalsEditor = ({
  value,
  onChange,
  disabled,
  invalidPaths,
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
  invalidPaths: string[][];
}) => {
  const { t } = useTenantTranslation();
  const { local_timezone: timezone } = useTenantData();
  const intervals = intervalsSchema.safeParse(value).data ?? [];
  const { cutoff, showOlder, revealOlder, toggle } = useOlderRows(
    t("autoShowOlderOutages")
  );
  const olderIndices = new Set(
    intervals.flatMap((interval, index) => {
      const start = dayjs(interval.start);
      const end = dayjs(interval.end);
      return start.isValid() &&
        end.isValid() &&
        end.isAfter(start) &&
        end.isBefore(cutoff) &&
        !invalidPaths.some((path) => path[0] === String(index))
        ? [index]
        : [];
    })
  );
  return (
    <Stack gap={2}>
      <Typography variant="body2">
        {t("autoLocalTime", { timezone })}
      </Typography>
      {olderIndices.size > 0 ? toggle : null}
      {intervals.map((interval, index) =>
        !showOlder && olderIndices.has(index) ? null : (
          // eslint-disable-next-line @eslint-react/no-array-index-key -- Controlled rows have no persisted identity.
          <Stack key={index} direction={{ xs: "column", sm: "row" }} gap={1}>
            {(["start", "end"] as const).map((boundary) => (
              <TextField
                key={boundary}
                label={t(boundary)}
                type="datetime-local"
                value={
                  dayjs(interval[boundary]).isValid()
                    ? dayjs(interval[boundary])
                        .tz(timezone)
                        .format("YYYY-MM-DDTHH:mm")
                    : ""
                }
                error={invalidPaths.some(
                  (path) => path[0] === String(index) && path[1] === boundary
                )}
                disabled={disabled}
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(event) => {
                  const timestamp = event.target.value
                    ? dayjs.tz(event.target.value, timezone)
                    : null;
                  revealOlder(timestamp);
                  onChange(
                    intervals.map((entry, position) =>
                      position === index
                        ? {
                            ...entry,
                            [boundary]: timestamp?.toISOString() ?? "",
                          }
                        : entry
                    )
                  );
                }}
              />
            ))}
            <IconButton
              aria-label={t("delete")}
              disabled={disabled}
              onClick={() => {
                onChange(intervals.filter((_, position) => position !== index));
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        )
      )}
      <Button
        disabled={disabled}
        onClick={() => {
          const start = dayjs().tz(timezone).startOf("day").add(1, "day");
          onChange([
            ...intervals,
            {
              start: start.toISOString(),
              end: start.add(1, "day").toISOString(),
            },
          ]);
        }}
      >
        {t("autoAddOutage")}
      </Button>
    </Stack>
  );
};

type Month = string;
const monthlyTargets = (value: unknown): Record<Month, number> =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? Object.fromEntries(
        Object.entries(value).filter(
          (entry): entry is [string, number] => typeof entry[1] === "number"
        )
      )
    : {};

export const MonthlyTargetsEditor = ({
  value,
  onChange,
  disabled,
  invalidPaths,
  schema,
  label,
  initialValue,
  mode,
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
  invalidPaths: string[][];
  schema: ParameterSchema;
  label: string;
  initialValue: unknown;
  mode: AutoRecipeMonthlyValueMode;
}) => {
  const { t } = useTenantTranslation();
  const { local_timezone: timezone } = useTenantData();
  const [duplicateMonth, setDuplicateMonth] = useState(false);
  const { today, cutoff, showOlder, revealOlder, toggle } = useOlderRows(
    t(mode === "carry_forward" ? "autoShowOlderPrices" : "autoShowOlderTargets")
  );
  const targets = monthlyTargets(value);
  const currentMonth = today.startOf("month").format("YYYY-MM-DD");
  const activePriceMonth =
    mode === "carry_forward"
      ? latestMonthlyChange({ month: currentMonth, changes: targets })
      : undefined;
  const olderMonths = new Set(
    Object.keys(targets).filter(
      (month) =>
        month !== activePriceMonth &&
        Number.isFinite(targets[month]) &&
        !invalidPaths.some((path) => path[0] === month) &&
        dayjs(month).isValid() &&
        dayjs.tz(month, timezone).endOf("month").isBefore(cutoff)
    )
  );
  const amountSchema =
    typeof schema.additionalProperties === "object"
      ? schema.additionalProperties
      : {};
  return (
    <Stack gap={2}>
      <MonthlyTargetsPlot
        mode={mode}
        targets={targets}
        defaultTarget={initialValue}
        onChange={onChange}
        disabled={disabled}
        label={label}
        timezone={timezone}
        minimum={amountSchema.minimum}
        maximum={amountSchema.maximum}
      />
      {duplicateMonth ? (
        <Alert severity="warning">{t("autoDuplicateMonth")}</Alert>
      ) : null}
      {olderMonths.size > 0 ? toggle : null}
      {Object.entries(targets)
        .filter(([month]) => showOlder || !olderMonths.has(month))
        .sort(([first], [second]) => first.localeCompare(second))
        .map(([month, target]) => (
          <Stack key={month} direction="row" gap={1}>
            <TextField
              label={t("month")}
              type="month"
              value={month.slice(0, 7)}
              disabled={disabled}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(event) => {
                if (!event.target.value) return;
                const nextMonth = `${event.target.value}-01`;
                revealOlder(dayjs.tz(nextMonth, timezone).endOf("month"));
                const duplicate = nextMonth !== month && nextMonth in targets;
                setDuplicateMonth(duplicate);
                if (!duplicate)
                  onChange(
                    Object.fromEntries(
                      Object.entries(targets).map(([key, amount]) => [
                        key === month ? nextMonth : key,
                        amount,
                      ])
                    )
                  );
              }}
            />
            <TextField
              label={label}
              type="number"
              value={Number.isFinite(target) ? target : ""}
              error={invalidPaths.some((path) => path[0] === month)}
              disabled={disabled}
              slotProps={{
                htmlInput: {
                  min: amountSchema.minimum,
                  max: amountSchema.maximum,
                  step: "any",
                },
              }}
              onChange={(event) => {
                onChange({
                  ...targets,
                  [month]:
                    event.target.value === ""
                      ? Number.NaN
                      : Number(event.target.value),
                });
              }}
            />
            <IconButton
              aria-label={t("delete")}
              disabled={disabled}
              onClick={() => {
                onChange(
                  Object.fromEntries(
                    Object.entries(targets).filter(([key]) => key !== month)
                  )
                );
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        ))}
      <Button
        disabled={disabled}
        onClick={() => {
          let month = dayjs().tz(timezone).startOf("month");
          while (month.format("YYYY-MM-DD") in targets)
            month = month.add(1, "month");
          onChange({
            ...targets,
            [month.format("YYYY-MM-DD")]:
              monthlyParameterValue({
                month: month.format("YYYY-MM-DD"),
                changes: targets,
                startingValue: initialValue,
                mode,
              }) ?? Number.NaN,
          });
        }}
      >
        {t(
          mode === "carry_forward"
            ? "autoAddPriceChange"
            : "autoAddMonthlyTarget"
        )}
      </Button>
    </Stack>
  );
};
